import { firstValueFrom } from 'rxjs';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AddressLocationManager, IDeliveryOrder, POST_NUMBER_LENGTHS } from '@cakeiteasy/web-utils';
import { CART_DELIVERY_TYPES, DELIVERY_ORDER_DELIVERY_TYPES } from '../../../cart/constants/common';
import { CitiesApiService, ICitySearch } from '../../../api-services/services/cities-api.service';
import { filter, tap } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'mail-delivery-to-door',
  templateUrl: './mail-delivery-to-door.component.html',
  styleUrls: ['./mail-delivery-to-door.component.scss']
})
export class MailDeliveryToDoorComponent implements OnInit {
  @Input() countryCode: string;
  @Input() description: string;
  @Input() deliveryOrder: IDeliveryOrder;
  /**Translation key for the confirm button — which step it says it leads to.*/
  @Input() nextBtnTextKey = 'orderProcess.delivery.goToPayment';
  @Input() nextBtnView = 'btn-pink';

  @Output() onNext = new EventEmitter<IDeliveryOrder>();

  busy: Promise<any>;
  postNumBusy: Promise<any>;
  deliveryForm: FormGroup;
  reserveCityName = '';
  canContinueDelivery = true;

  get isCityValid() {
    return this.deliveryForm
      && this.deliveryForm.get('deliveryPostNumber').valid
      && this.deliveryForm.get('deliveryCity').valid;
  }

  constructor(private addressManager: AddressLocationManager,
              private citiesApi: CitiesApiService,
              private formBuilder: FormBuilder) {
  }

  public ngOnInit(): void {
    this.deliveryForm = this.getDeliveryForm();

    if (this.deliveryOrder) {
      this.deliveryForm.patchValue(this.deliveryOrder);
    }

    this.subscribeOnPostNumber();
  }

  async confirm() {
    await (this.busy = this.patchAddressCoordinates());

    const deliveryType = DELIVERY_ORDER_DELIVERY_TYPES.MAIL;
    const type = CART_DELIVERY_TYPES.DELIVERY;

    this.onNext.emit({
      ...this.deliveryOrder,
      ...this.deliveryForm.getRawValue(),
      externalDeliveryFee: 0,
      outlet: null,
      deliveryType, type
    });
  }

  private findDeliveryLocation(postNumber: string): Promise<ICitySearch | null> {
    return this.citiesApi.searchLocationByPostNumber(this.countryCode, postNumber);
  }

  private getDeliveryForm() {
    const postNumValidators = [
      Validators.required,
      Validators.minLength(POST_NUMBER_LENGTHS[this.countryCode])
    ];

    return this.formBuilder.group({
      deliveryCity: ['', [Validators.required]],
      deliveryAddress: ['', [Validators.required]],
      deliveryPostNumber: ['', postNumValidators],
      deliveryUserName: ['', [Validators.required]],
      deliveryLatitude: [{ value: '', disabled: true }, [Validators.required]],
      deliveryLongitude: [{ value: '', disabled: true }, [Validators.required]],
    });
  }

  private subscribeOnPostNumber() {
    const postNumCtrl = this.deliveryForm.get('deliveryPostNumber');
    const cityCtrl = this.deliveryForm.get('deliveryCity');

    postNumCtrl.valueChanges
      .pipe(tap(() => {
        this.canContinueDelivery = true;
        this.reserveCityName = '';
        cityCtrl.patchValue(null);
        this.deliveryForm.patchValue({
          deliveryLatitude: null,
          deliveryLongitude: null,
        });
      }))
      .pipe(filter(() => postNumCtrl.valid))
      .subscribe(async (postNum) => {
        const citySearch = await (this.postNumBusy = this.findDeliveryLocation(postNum));

        if (!citySearch) {
          this.canContinueDelivery = false;
          return;
        }

        const city = citySearch.placeName || citySearch.cityName;
        this.reserveCityName = city;
        this.canContinueDelivery = true;
        cityCtrl.patchValue(city);
      });
  }

  private async patchAddressCoordinates(): Promise<void> {
    const { deliveryAddress, deliveryPostNumber, deliveryCity } = this.deliveryForm.getRawValue();
    const address = `${deliveryAddress}, ${deliveryPostNumber} ${deliveryCity}`;
    const { location: { lat, lng } } = await firstValueFrom(this.addressManager.getLocationInfo(address, this.countryCode));
    this.deliveryForm.patchValue({ deliveryLatitude: lat, deliveryLongitude: lng });
  }
}
